<script setup>

import {computed, ref, watch} from "vue";
import {config} from "@/config";

const props = defineProps({
  files: {
    type: Array,
    default: () => [],
  },
  showDelete: {
    type: Boolean,
    default: true
  },
  height: {
    type: String,
    default: '29vh'
  },
  fileUrl: {
    type: Function,
  },
  currentId: {
    type: Number
  },
})

const emit = defineEmits(['delete:file'])

const currentIndex = ref(0)

const displayedFiles = computed(() => props.files.filter(f => !f.toBeRemoved))
const showArrows = computed(() => displayedFiles.value.length > 1)

watch(
    () => displayedFiles.value,
    () => fixCurrentIndex()
)

watch(
    () => props.currentId,
    id => {
      if (id) {
        currentIndex.value = displayedFiles.value.findIndex(f => f.id === id) ?? 0
      }
    }
)

/**
 * If this is a new file not-yet uploaded, embed the selected image into src as blob.
 * Otherwise, use url attribute.
 */
function getFileUrl(file) {
  if (file.toBeUploaded && file.raw?.type.startsWith('image')) {
    return URL.createObjectURL(file.raw)
  }

  if (props.fileUrl) {
    const url = props.fileUrl(file)
    if(url){
      return url.startsWith('http') ? url : config.serverUrl + url
    } else {
      return null
    }
  }

  return null
}

function fixCurrentIndex() {
  if (currentIndex.value >= displayedFiles.value.length) {
    showLast()
  }
}

function showLast() {
  currentIndex.value = Math.max(0, displayedFiles.value.length - 1)
}

function deleteCurrentFile() {
  const file = displayedFiles.value[currentIndex.value]
  emit('delete:file', file.id)
}

function openImage(file) {
  const url = getFileUrl(file)
  window.open(url, '_blank')
}
</script>

<template>
  <v-card data-cy="gallery" v-if="displayedFiles.length > 0">
    <v-carousel
        v-model="currentIndex"
        :show-arrows="showArrows"
        :hide-delimiters="!showArrows"
        :height="height"
    >
      <v-btn
          v-if="showDelete"
          icon="delete"
          color="error"
          size="small"
          density="comfortable"
          @click="deleteCurrentFile"
          class="delete-image"
          data-cy="delete-image"
      ></v-btn>
      <v-carousel-item
          v-for="(file, index) in displayedFiles"
          :data-cy="'gallery-img-' + index"
          :key="file.id"
          :src="getFileUrl(file)"
          reverse-transition="fade-transition"
          transition="fade-transition"
          class="cursor-pointer"
          @click="openImage(file)"
      ></v-carousel-item>
    </v-carousel>
  </v-card>
</template>

<style scoped>
.delete-image {
  position: absolute;
  z-index: 9;
  right: 1em;
  top: 1em;
}
</style>